/**
 * Authentication module for Ragsmith remote access
 */

let authToken = null;
let modalElements = {};
let initialized = false;

/**
 * Initialize auth module - call early in app init
 */
export function initAuth() {
    // On first init, try to restore from sessionStorage
    // On subsequent calls (after login), authToken is already set by handleLogin()
    if (!authToken) {
        authToken = sessionStorage.getItem('ragsmith_auth_token');
    }

    // Only create modal and bind events once
    if (initialized) {
        return;
    }
    initialized = true;

    // Inject login modal HTML
    createLoginModal();

    // Cache elements
    modalElements = {
        overlay: document.getElementById('login-overlay'),
        panel: document.getElementById('login-panel'),
        passwordInput: document.getElementById('login-password'),
        submitBtn: document.getElementById('login-submit'),
        errorMsg: document.getElementById('login-error')
    };

    // Bind events
    modalElements.submitBtn.addEventListener('click', handleLogin);
    modalElements.passwordInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleLogin();
    });

    // Password visibility toggle
    const toggleBtn = document.getElementById('login-toggle-pwd');
    toggleBtn.addEventListener('click', () => {
        const input = modalElements.passwordInput;
        const isPassword = input.type === 'password';
        input.type = isPassword ? 'text' : 'password';
        toggleBtn.querySelector('.eye-open').style.display = isPassword ? 'none' : '';
        toggleBtn.querySelector('.eye-closed').style.display = isPassword ? '' : 'none';
    });
}

/**
 * Create login modal HTML structure
 */
function createLoginModal() {
    const html = `
        <div id="login-overlay" class="login-overlay">
            <div id="login-panel" class="login-panel">
                <div class="login-header">
                    <h1 class="login-title">Ragsmith</h1>
                    <p class="login-subtitle">Remote Access</p>
                </div>
                <div class="login-body">
                    <div class="login-field">
                        <input type="password" id="login-password"
                               placeholder="Enter password"
                               autocomplete="current-password">
                        <button type="button" id="login-toggle-pwd" class="login-toggle-pwd" tabindex="-1" aria-label="Toggle password visibility">
                            <svg class="eye-open" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            <svg class="eye-closed" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" style="display:none">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                        </button>
                    </div>
                    <div id="login-error" class="login-error"></div>
                    <button id="login-submit" class="login-btn">Sign In</button>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('afterbegin', html);
}

/**
 * Check if authentication is required
 * @returns {Promise<boolean>} True if app can proceed, false if login needed
 */
export async function checkAuthRequired() {
    try {
        const res = await fetch('/auth/status', {
            headers: authToken ? { 'X-Auth-Token': authToken } : {}
        });
        const data = await res.json();

        if (!data.requires_auth || data.authenticated) {
            return true;  // Proceed with app init
        }

        // Show login modal
        showLoginModal();
        return false;  // Don't proceed until authenticated
    } catch (e) {
        console.error('Auth check failed:', e);
        return true;  // Allow on error (fail open for localhost)
    }
}

/**
 * Show the login modal
 */
function showLoginModal() {
    modalElements.overlay.classList.add('active');
    modalElements.passwordInput.focus();
}

/**
 * Hide the login modal
 */
function hideLoginModal() {
    modalElements.overlay.classList.remove('active');
}

/**
 * Handle login form submission
 */
async function handleLogin() {
    const password = modalElements.passwordInput.value;
    modalElements.errorMsg.textContent = '';
    modalElements.submitBtn.disabled = true;
    modalElements.submitBtn.textContent = 'Signing in...';

    try {
        const res = await fetch('/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password })
        });
        const data = await res.json();

        if (data.success) {
            authToken = data.token;
            if (authToken) {
                sessionStorage.setItem('ragsmith_auth_token', authToken);
            }
            hideLoginModal();
            // Trigger app initialization
            window.dispatchEvent(new Event('auth-success'));
        } else {
            modalElements.errorMsg.textContent = data.error || 'Invalid password';
            modalElements.passwordInput.select();
        }
    } catch (e) {
        modalElements.errorMsg.textContent = 'Connection failed';
    } finally {
        modalElements.submitBtn.disabled = false;
        modalElements.submitBtn.textContent = 'Sign In';
    }
}

/**
 * Get current auth token for API requests
 * @returns {string|null} Auth token or null
 */
export function getAuthToken() {
    // If authToken not set yet (e.g., during early init), try sessionStorage
    if (!authToken) {
        authToken = sessionStorage.getItem('ragsmith_auth_token');
    }
    return authToken;
}

/**
 * Clear authentication (logout)
 */
export function clearAuth() {
    authToken = null;
    sessionStorage.removeItem('ragsmith_auth_token');
}
